/**
 * Pure helper functions for Trends tab computations.
 * Kept separate from components so the logic is easy to read and
 * verify by inspection. No React / SVG / DOM imports here.
 */

import type { HandoffLatency } from "../../../api/work";

/**
 * The hand-off for each gap between adjacent cells: entry `i` is
 * `states[i] → states[i + 1]`, or `null` when the route sent no such pair.
 *
 * The route computes one latency per adjacent pair of the lane's
 * workflow, and the cells are that same workflow in the same order, so
 * every gap normally has one. Names compare case-insensitively because
 * the route's SQL does (`COLLATE NOCASE`) — a lane configured "In
 * progress" and a pair reported as "In Progress" are the same gap.
 */
export function handoffsBetween(
  states: string[],
  latencies: HandoffLatency[],
): Array<HandoffLatency | null> {
  const key = (from: string, to: string) =>
    `${from.toLowerCase()}\u0000${to.toLowerCase()}`;
  const byPair = new Map(latencies.map((h) => [key(h.from_state, h.to_state), h]));
  return states
    .slice(0, -1)
    .map((from, i) => byPair.get(key(from, states[i + 1] ?? "")) ?? null);
}

/**
 * A queue time short enough for a 6px label: "<1h", "5h", "2.5d", "12d".
 * Hours up to two days, then days. A negative or non-finite value reads
 * "?" rather than a number nobody should act on.
 */
export function formatQueueHours(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return "?";
  if (hours < 1) return "<1h";
  // Each switch is decided on the value as it will be printed, so 47.6h
  // reads "2.0d" rather than "48h", and 9.96 days "10d" rather than "10.0d".
  const wholeHours = Math.round(hours);
  if (wholeHours < 48) return `${wholeHours}h`;
  const days = hours / 24;
  const tenths = Math.round(days * 10) / 10;
  return tenths < 10 ? `${tenths.toFixed(1)}d` : `${Math.round(days)}d`;
}

/** Arithmetic mean of a numeric array. Returns 0 for empty arrays. */
export function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

/** Median of a numeric array. Returns 0 for empty arrays. */
export function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  // `?? 0` on every index: `noUncheckedIndexedAccess` types these as
  // possibly-undefined even after the length check above, and asserting
  // with `!` would silence a real off-by-one alongside the false one. The
  // empty case already returned, so the fallback is unreachable.
  const hi = sorted[mid] ?? 0;
  const lo = sorted[mid - 1] ?? hi;
  return sorted.length % 2 === 0 ? (lo + hi) / 2 : hi;
}

/** Sum of a numeric array. */
export function sum(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0);
}

/**
 * Bottleneck detection for a state cell in a lane.
 *
 * A cell qualifies when:
 *   - count == max(lane_counts)
 *   - count >= 2 * median(lane_counts)
 *   - count >= 3  (absolute floor — avoids flagging noise)
 *
 * Returns true for the single dominant cell in a lane, provided the
 * dominance threshold is met.
 */
export function isBottleneck(count: number, laneCounts: number[]): boolean {
  if (laneCounts.length === 0) return false;
  const m = Math.max(...laneCounts);
  if (count !== m) return false;
  if (count < 3) return false;
  const med = median(laneCounts);
  return med === 0 ? true : count >= 2 * med;
}

/**
 * Week-over-week delta as a signed fraction.
 *
 *   wowDelta([a, b]) === (b - a) / a
 *
 * When the previous week is 0, returns null (caller should render an em-dash).
 * When the series has fewer than 2 points, returns null.
 */
export function wowDelta(series: number[]): number | null {
  if (series.length < 2) return null;
  const prev = series[series.length - 2];
  const curr = series[series.length - 1];
  // `null` for a missing endpoint as well as for a zero denominator: both
  // mean "no delta to report", and the caller already renders an em-dash.
  if (prev === undefined || curr === undefined || prev === 0) return null;
  return (curr - prev) / prev;
}

/**
 * Classify a week-over-week delta for a given metric.
 *
 *   "good"    — trending in the helpful direction, |delta| >= 0.05
 *   "bad"     — trending in the harmful direction, |delta| >= 0.05
 *   "neutral" — |delta| < 0.05 (noise band)
 *   "none"    — delta is null (missing data)
 *
 * For "throughput", up is good. For "cycle_time" and "backlog_size", down is good.
 */
export type TrendMetric = "throughput" | "cycle_time" | "backlog_size";
export type TrendClass = "good" | "bad" | "neutral" | "none";

export function classifyTrend(delta: number | null, metric: TrendMetric): TrendClass {
  if (delta === null) return "none";
  if (Math.abs(delta) < 0.05) return "neutral";
  const upIsGood = metric === "throughput";
  if (delta > 0) return upIsGood ? "good" : "bad";
  return upIsGood ? "bad" : "good";
}

/**
 * Format a fractional delta as a percentage label.
 *   formatPct(0.25)   // "25%"
 *   formatPct(-0.22)  // "22%" (direction shown separately via arrow)
 *   formatPct(null)   // "—"
 */
export function formatPct(d: number | null): string {
  if (d === null) return "—";
  return `${Math.round(Math.abs(d) * 100)}%`;
}

/** Arrow glyph corresponding to a signed delta. Returns "≈" for neutral. */
export function deltaArrow(d: number | null, neutralBand = 0.05): string {
  if (d === null) return "—";
  if (Math.abs(d) < neutralBand) return "≈";
  return d > 0 ? "↑" : "↓";
}
